import {AxiosError} from "axios";
import {GoogleDocs as LogsIcon} from "iconoir-react";
import {useEffect, useState} from 'react'
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Row from 'react-bootstrap/Row';
import Table from 'react-bootstrap/Table';
import ToggleButton from 'react-bootstrap/ToggleButton';
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {ApiConfig} from "../api/admin/ApiConfig";
import {LogApi, LogTarget} from "../api/admin/generated";
import {Pager} from "../common/Pager";
import {PageTitle} from "../common/PageTitle";
import {ReloadButton} from "../common/ReloadButton";
import {RenderAlert} from "../common/RenderAlert";
import {RowsPerPage} from "../common/RowsPerPage";
import {useSpinnerStatusContext} from "../hook/UseSpinnerStatusContext";
import {DateUtils} from "../utils/DateUtils";
import {Highlighter} from "../utils/Highlighter";
import {logger} from "../utils/Logger";
import {Utils} from "../utils/Utils";

/*
 * REST api for accessing the back-end
 */
const logApi = new LogApi(ApiConfig.config());

export interface LogLine {
    __REALTIME_TIMESTAMP: string,
    SYSLOG_IDENTIFIER: string
    MESSAGE: string,
}

export const LogView = () => {
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated or to some other page
     * if the dialog is closed
     */
    const navigate = useNavigate();
    /**
     * The log target to view
     */
    const [logTarget, setLogTarget] = useState<LogTarget>(LogTarget.Mta);
    /**
     * If true, the latest log lines will be shown first
     */
    const [reverse, setReverse] = useState<boolean>(true);
    /**
     * active filter
     */
    const [filter, setFilter] = useState<string>("");
    /**
     * new filter
     */
    const [newFilter, setNewFilter] = useState<string>("");
    /**
     * since field
     */
    const [since, setSince] = useState<string>("");
    /**
     * new since field
     */
    const [newSince, setNewSince] = useState<string>("");
    /**
     * until field
     */
    const [until, setUntil] = useState<string>("");
    /**
     * new until field
     */
    const [newUntil, setNewUntil] = useState<string>("");
    /**
     * log lines count
     */
    const [logLinesCount, setLogLinesCount] = useState<number>(0);
    /**
     * log lines
     */
    const [logLines, setLogLines] = useState<LogLine[]>([]);
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * The index of the first page to show
     */
    const [currentPage, setCurrentPage] = useState<number>(1);
    /**
     * The max number of domains to show
     */
    const [rowsPerPage, setRowsPerPage] = useState<number>(40);
    /**
     * If true, the items will be reloaded from the back-end
     */
    const [forceReload, setForceReload] = useState<boolean>(false);
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * i18next translation hook
     */
    const {t} = useTranslation();

    useEffect(() =>
    {
        logger.debug("Load log line count")

        // reset state when loading new data
        setForceReload(false);
        setCurrentPage(1);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        logApi.logControllerGetLogLinesCount(logTarget, filter, since, until)
            .then(response =>
            {
                setLogLinesCount(response.data)
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting log line count", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [navigate,
        forceReload,
        setSpinnerStateCounter,
        logTarget,
        filter,
        since,
        until,
        t
    ]);

    useEffect(() =>
    {
        logger.debug("Load log lines")

        // reset state when loading new data
        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        logApi.logControllerGetLogLines(logTarget, reverse, (currentPage - 1) * rowsPerPage, rowsPerPage, filter, since, until)
            .then(response =>
            {
                setLogLines(response.data as unknown as LogLine[])
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting log lines", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [navigate,
        forceReload,
        setSpinnerStateCounter,
        logTarget,
        reverse,
        currentPage,
        rowsPerPage,
        filter,
        since,
        until,
        t
    ]);

    const highlight = (log : string) => {

        let result = Highlighter.highlight(log);

        // add additional rules based on the log target
        if (logTarget === LogTarget.Mta)
        {
            result = result
                .replace("Connection refused", "<span class='highlight-danger'>$&</span>")
                .replace(/(warning):/g, "<span class='highlight-danger'>$1</span>:")
                .replace(/^(NOQUEUE):/g, "<span class='highlight-danger'>$1</span>:")
                .replace(/^[a-zA-Z0-9]+:\s/g, "<span class='highlight-info'>$&</span>")
        }
        else if (logTarget === LogTarget.Mpa) {
            result = result
                .replace(/(MailID: )([a-z0-9-]+)/g, "$1<span class='highlight-info'>$2</span>")
                .replace(/\| (WARN)\s+/g, "| <span class='highlight-danger'>$1</span> ")
                .replace(/\| (ERROR)\s+/g, "| <span class='highlight-danger'>$1</span> ")
                .replace(/\| INFO\s+(Admin logged in)/g, "| INFO <span class='highlight-danger'>$1</span> ")
                .replace(/\| INFO\s+(incoming)/g, "| INFO <span class='highlight-incoming'>$1</span> ")
        }

        return result;
    }

    return (
        <>
            <Row>
                <div className="d-flex flex-row align-items-center border-bottom pb-2 mb-4">
                    <div className="d-flex justify-content-start">
                        <PageTitle title={t("logview.title")} icon={<LogsIcon />}/>
                    </div>
                    <div className="d-flex flex-fill justify-content-end">
                        <ReloadButton onClickReload={() => setForceReload(true)} />
                    </div>
                </div>
            </Row>

            <Row>
                <Col>
                    <RenderAlert message={errorMessage} setMessage={setErrorMessage} tooltipPlacement={"bottom"}/>
                </Col>
            </Row>

            <Row>
                <div className="d-flex flex-row align-items-center mb-4">
                    <div className="d-flex justify-content-start">
                        <Form.Select value={logTarget}
                                     onChange={(e) => setLogTarget(Utils.stringToEnum(LogTarget, e.target.value as LogTarget))}>
                            <option value="MTA">{t("logview.mta")}</option>
                            <option value="MPA">{t("logview.mpa")}</option>
                            <option value="IMAP">{t("logview.imap")}</option>
                        </Form.Select>
                    </div>
                    <div className="d-flex flex-fill justify-content-end align-items-center">
                        <div className="d-xl-flex">
                            <ToggleButton
                                size="sm"
                                id="toggle-check"
                                type="checkbox"
                                variant="outline-secondary"
                                checked={reverse}
                                value={1}
                                onChange={(e) => setReverse(e.target.checked)}
                            >
                                {t("logview.reverse")}
                            </ToggleButton>


                            <div className="ms-4"/>

                            <InputGroup>
                                <Form.Control
                                              aria-label="Search value"
                                              type="search"
                                              placeholder={t("logview.search")}
                                              value={newFilter}
                                              onChange={e => setNewFilter(e.target.value)}
                                />
                                <Form.Control
                                              aria-label="Since value"
                                              type="search"
                                              placeholder={t("logview.since")}
                                              value={newSince}
                                              onChange={e => setNewSince(e.target.value)}
                                />
                                <Form.Control
                                              aria-label="Until value"
                                              type="search"
                                              placeholder={t("logview.until")}
                                              value={newUntil}
                                              onChange={e => setNewUntil(e.target.value)}
                                />
                                <Button
                                        variant="primary"
                                        onClick={() => {setFilter(newFilter); setSince(newSince); setUntil(newUntil)}}
                                        disabled={filter === newFilter && since === newSince && until === newUntil}>
                                    {t("logview.search")}
                                </Button>
                            </InputGroup>
                        </div>
                    </div>
                </div>
            </Row>

            <Row className="h-100 w-100 overflow-auto">
                <Col>
                    <Table bordered hover>
                        <thead className="sticky-top">
                        <tr className="text-nowrap">
                            <th>{t("logview.timestamp")}</th>
                            <th>{t("logview.identifier")}</th>
                            <th>{t("logview.message")}</th>
                        </tr>
                        </thead>
                        <tbody>
                        {logLines?.map((ll: LogLine, index) => (
                            <tr key={index}>
                                <td className={"user-select-all"}> {DateUtils.timestampToUTCString(Number(ll.__REALTIME_TIMESTAMP)/1000)} </td>
                                <td className={"user-select-all"}> {ll.SYSLOG_IDENTIFIER} </td>
                                {/*
                                    use MESSAGE.toString() because sometimes the result is not a string
                                    this can happen if "invalid" log lines are written to the journal
                                */}
                                <td> <div dangerouslySetInnerHTML={{__html: highlight(ll.MESSAGE?.toString())}} /> </td>
                            </tr>
                        ))}
                        </tbody>
                    </Table>
                </Col>
            </Row>

            <Row className="mt-4 align-items-baseline">
                <div className="col-4 d-flex">
                    <div className="d-flex justify-content-start align-items-center d-none d-md-inline-flex me-4">
                        {logLinesCount} {t("common.results")}
                    </div>

                    <div className="d-flex justify-content-start align-items-center">
                        <RowsPerPage rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage}
                                     setCurrentPage={setCurrentPage}/>
                    </div>
                </div>
                <div className="col-4">
                    <div className="d-flex justify-content-center">
                        <Pager itemCount={logLinesCount}
                               currentPage={currentPage}
                               setCurrentPage={setCurrentPage}
                               maxItemsPerPage={rowsPerPage}
                        />
                    </div>
                </div>
            </Row>
        </>
    );
}
